import React, { FunctionComponent, useCallback, useState } from 'react';
import Head from 'next/head';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { H1, Text, H3, H2 } from 'components/Typography/Typography';
import { Layout, LayoutWrapper } from 'components/Layout/Layout';
import styled, { css } from 'styled-components';
import { Header } from 'components/Layout/Header';
import { useRouter } from 'next/router';
import { Footer } from 'components/Layout/Footer';
import { SCHOLARS2025 } from '../assets/scholars';
import { ActionButton } from '../components/Buttons/ActionButton';
import { scrollTop } from '../utils';
import ArrowTop from '../public/images/arrow-top.svg';

const getBgSize = (name: string) => {
  switch (name) {
    case 'Step by Step':
      return '90%';
    case 'Fins Off':
      return '90%';
    case 'Seoul Foreign School HS':
      return '90%';
    case 'Keystone Oak':
      return '90%';
    case 'Avis':
      return '90%';
    default:
      return null;
  }
};

const Scholars: FunctionComponent = () => {
  const router = useRouter();
  const onRoute = useCallback(
    (path) => {
      router.push(path);
      scrollTop();
    },
    [router],
  );
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRegionIndex, setSelectedRegionIndex] = useState<number | null>(
    null,
  );
  const [selectedUserIndex, setSelectedUserIndex] = useState<number | null>(
    null,
  );
  const [openedUser, setOpenedUser] = useState(null);

  const handleNext = () => {
    if (selectedRegionIndex === null || selectedUserIndex === null) return;

    let newRegionIndex = selectedRegionIndex;
    let membersWithDescription = SCHOLARS2025[newRegionIndex].teams.filter(
      (member) => member.description,
    );

    const filteredUserIndex = membersWithDescription.findIndex(
      (member) => member.name === selectedUser.name,
    );

    let newFilteredUserIndex = filteredUserIndex + 1;

    while (newFilteredUserIndex >= membersWithDescription.length) {
      newRegionIndex = (newRegionIndex + 1) % SCHOLARS2025.length;
      membersWithDescription = SCHOLARS2025[newRegionIndex].teams.filter(
        (member) => member.description,
      );
      newFilteredUserIndex = 0;
    }

    setSelectedRegionIndex(newRegionIndex);
    setSelectedUserIndex(
      SCHOLARS2025[newRegionIndex].teams.findIndex(
        (member) =>
          member.name === membersWithDescription[newFilteredUserIndex].name,
      ),
    );
    setSelectedUser(membersWithDescription[newFilteredUserIndex]);
  };

  const handlePrev = () => {
    if (selectedRegionIndex === null || selectedUserIndex === null) return;

    let newRegionIndex = selectedRegionIndex;
    let membersWithDescription = SCHOLARS2025[newRegionIndex].teams.filter(
      (member) => member.description,
    );

    const filteredUserIndex = membersWithDescription.findIndex(
      (member) => member.name === selectedUser.name,
    );

    let newFilteredUserIndex = filteredUserIndex - 1;

    while (newFilteredUserIndex < 0) {
      newRegionIndex =
        (newRegionIndex - 1 + SCHOLARS2025.length) % SCHOLARS2025.length;
      membersWithDescription = SCHOLARS2025[newRegionIndex].teams.filter(
        (member) => member.description,
      );
      newFilteredUserIndex = membersWithDescription.length - 1;
    }

    setSelectedRegionIndex(newRegionIndex);
    setSelectedUserIndex(
      SCHOLARS2025[newRegionIndex].teams.findIndex(
        (member) =>
          member.name === membersWithDescription[newFilteredUserIndex].name,
      ),
    );
    setSelectedUser(membersWithDescription[newFilteredUserIndex]);
  };

  return (
    <Layout>
      <Head>
        <title>The Earth Prize - Scholars</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:title" content="The Earth Prize - Scholars" />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header />
      <ScholarsWrapper>
        <H1>
          THE EARTH PRIZE 2025
          <br />
          SCHOLARS
        </H1>
        <Text>
          This year for the first time, seven Regional Winners (
          <b>
            Oceania & Southeast Asia, Asia, Middle East, Africa, Europe, Central
            & South America, North America
          </b>
          ) will be chosen by The Earth Prize Adjudicating Panel from the group
          of Scholars. Meet The Earth Prize 2025 Scholars below.
        </Text>
        <div className="pt-[100px] pb-[20px]">
          {SCHOLARS2025.map((region, regionIndex) => (
            <div key={region.name}>
              <H2
                className="!text-center !mb-[-50px] !mt-[10px] !text-[24px] md:!text-[38px]"
                color="#1d8676"
              >
                {region.name}
              </H2>
              <UsersWrapper>
                {region.teams.slice().map((member, memberIndex) => (
                  <User
                    key={member.name}
                    opened={openedUser?.name === member.name}
                  >
                    {openedUser?.name === member.name && (
                      <UserOverview>
                        <Close
                          onClick={() => {
                            setSelectedUser(null);
                            setSelectedRegionIndex(null);
                            setSelectedUserIndex(null);
                            setOpenedUser(null);
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes as IconProp} />
                        </Close>
                        <OverviewPersonImage photo={selectedUser.photo} />
                        <OverviewContent>
                          <OverviewName>{selectedUser.name}</OverviewName>
                          {selectedUser.description && (
                            <OverviewBio>
                              <Quote /> {selectedUser.description} <QuoteEnd />
                            </OverviewBio>
                          )}
                          <ButtonsWrapper>
                            <ActionButton
                              isSmall
                              onClick={handlePrev}
                              background="#afe0d7"
                              color="black"
                            >
                              <FontAwesomeIcon icon={faArrowLeft as IconProp} />
                            </ActionButton>
                            <ActionButton
                              isSmall
                              onClick={handleNext}
                              background="#afe0d7"
                              color="black"
                            >
                              <FontAwesomeIcon
                                icon={faArrowRight as IconProp}
                              />
                            </ActionButton>
                          </ButtonsWrapper>
                        </OverviewContent>
                      </UserOverview>
                    )}
                    <UserInner opened={openedUser?.name === member.name}>
                      <div>
                        <PersonImage
                          photo={member.photo}
                          backgroundSize={getBgSize(member.name)}
                        >
                          {!member.photo && <Text>Coming soon</Text>}
                        </PersonImage>
                        <UserContent>
                          <Name>{member.name}</Name>
                          <School>{member.school}</School>
                        </UserContent>
                      </div>
                      <div>
                        <Country>{member.country}</Country>
                        {member.description ? (
                          <ActionButton
                            onClick={() => {
                              setSelectedRegionIndex(regionIndex);
                              setSelectedUserIndex(memberIndex);
                              setSelectedUser(member);
                              setOpenedUser(member);
                            }}
                            background="black"
                            color="white"
                            style={{
                              height: '37px',
                              textTransform: 'initial',
                              padding: '12px 29px',
                              fontSize: '12px',
                            }}
                          >
                            More info
                          </ActionButton>
                        ) : (
                          <ActionButton
                            onClick={() => {}}
                            background="black"
                            color="white"
                            style={{
                              height: '37px',
                              textTransform: 'initial',
                              padding: '12px 29px',
                              fontSize: '12px',
                              cursor: 'auto',
                            }}
                          >
                            Coming soon
                          </ActionButton>
                        )}
                      </div>
                    </UserInner>
                  </User>
                ))}
              </UsersWrapper>
            </div>
          ))}
        </div>
        <PrevYearLink color="black" onClick={() => onRoute('/2024-scholars')}>
          Meet The Earth Prize 2024 Scholars &nbsp;&nbsp;
          <FontAwesomeIcon icon={faArrowRight as IconProp} />
        </PrevYearLink>
        <BottomButton onClick={() => scrollTop()}>
          <ArrowTop />
        </BottomButton>
      </ScholarsWrapper>
      <Footer />
    </Layout>
  );
};

interface IImage {
  photo: string;
  backgroundSize?: string;
}

const PrevYearLink = styled.h2<{ color?: string }>`
  ${({ color = '#e2a375', theme: { Consts } }) => css`
    font-size: 18px;
    color: ${color};
    font-weight: bold;
    margin-top: 50px;
    text-align: center;
    cursor: pointer;
    display: flex;
    justify-content: center;
    width: 100%;

    &:hover {
      text-decoration: underline;
    }

    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 14px;
      margin-top: 30px;
      padding-bottom: 50px;
    }
  `}
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  grid-gap: 24px;
  margin-top: 50px;
`;

const Close = styled.button`
  position: absolute;
  right: 50px;
  border: 0;
  box-shadow: none;
  top: 40px;
  font-size: 20px;
  cursor: pointer;
  background-color: transparent;
  color: #afe0d7;
`;

const UserOverview = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    grid-gap: 100px;
    position: absolute;
    align-items: center;
    grid-template-columns: 35% 1fr;
    background-color: black;
    left: 0;
    transform: translateY(-480px);
    width: 100vw;
    min-height: 550px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 1fr;
      width: 100vw;
      position: relative;
      grid-gap: 20px;
      padding-top: 100px;
      transform: translateY(0);
      margin-bottom: 50px;
      min-height: 0;
    }
  `}
`;

const OverviewContent = styled.div`
  ${({ theme: { Consts } }) => css`
    padding: 38px 50px 38px 0;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 38px 30px;
    }
  `}
`;

const User = styled.div<{ opened: boolean }>`
  ${({ opened, theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 90px;
    width: 220px;
    flex-shrink: 0;
    margin-top: ${opened ? `550px` : '0'};

    @media (max-width: ${Consts.mobileBreakPoint}) {
      margin-bottom: 30px;
      margin-top: 0;
    }
  `}
`;

const UserInner = styled.div<{ opened: boolean }>`
  ${({ opened, theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: ${opened ? '460px' : '100%'};
    max-height: 460px;
    gap: 10px;
    padding: 0 15px;

    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: auto;
      max-height: none;
    }
  `}
`;

const UserContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Name = styled(H3)`
  ${({ theme: { Consts } }) => css`
    color: black;
    text-align: center;
    font-size: 18px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 16px;
    }
  `}
`;

const School = styled(Text)`
  ${({ theme: { Consts } }) => css`
    color: black;
    text-align: center;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 16px;
    }
  `}
`;

const Country = styled(Text)`
  ${({ theme: { Consts } }) => css`
    color: #2e5b66 !important;
    text-align: center;
    font-weight: bold;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 16px;
    }
  `}
`;

const OverviewName = styled(H3)`
  color: #afe0d7;
  font-size: 22px;
  margin-bottom: 20px;
`;

const OverviewBio = styled(Text)`
  color: #afe0d7 !important;
  font-size: 14px;
  position: relative;
  white-space: pre-line;
`;

const OverviewPersonImage = styled.div<IImage>`
  ${({ photo, backgroundSize, theme: { Consts } }) => css`
    background-image: ${`url(${photo})`};
    background-size: 100%;
    background-position: center;
    background-repeat: no-repeat;
    background-color: black;
    height: 100%;
    width: 100%;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: 400px;
    }
  `}
`;

const PersonImage = styled.div<IImage>`
  ${({ photo, theme: { Consts }, backgroundSize }) => css`
    background-image: ${`url(${photo})`};
    background-size: 100%;
    background-color: #fff;
    background-position: center;
    border: 1px solid #ddd;
    background-repeat: no-repeat;
    height: 170px;
    width: 170px;
    flex-shrink: 0;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    p {
      text-align: center;
      width: 100%;
      font-weight: bold;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: 130px;
      width: 130px;
    }
  `}
`;

const ScholarsWrapper = styled(LayoutWrapper)`
  ${({ theme: { Consts } }) => css`
    padding: 246px 80px 100px;
    min-height: 100vh;
    text-align: center;
    background-color: #fff;
    position: relative;
    color: black;
    @media (max-width: ${Consts.tabletBreakPoint}) {
      padding: 100px 30px 0;
    }
    > h1 {
      margin-bottom: 34px;
      color: black;
      @media (max-width: ${Consts.tabletBreakPoint}) {
        margin-bottom: 20px;
        font-size: 28px;
      }
    }

    p {
      margin-bottom: 0;
      color: black;
    }
  `}
`;

const UsersWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    align-items: stretch;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 1250px;
    margin: 130px auto 0;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 1fr;
      grid-gap: 50px;
      margin-top: 70px;
    }
  `}
`;

const Quote = styled.span`
  background-image: url('/images/quoteBlue.svg');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  height: 25px;
  width: 25px;
  display: inline-block;
  position: relative;
  top: 1px;
  left: -3px;
`;

const QuoteEnd = styled.span`
  background-image: url('/images/quoteBlue.svg');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  height: 25px;
  width: 25px;
  display: inline-block;
  position: relative;
  top: 1px;
  left: 3px;
  transform: rotate(180deg);
`;

const BottomButton = styled.button`
  ${({ theme: { Consts } }) => css`
    color: black;
    background: transparent;
    border: 2px solid black;
    border-radius: 20px 20px 20px 0;
    height: 30px;
    width: 52px;
    cursor: pointer;
    position: absolute;
    right: 50px;
    bottom: 30px;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      transform: scale(0.8);
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: 20px;
      width: 42px;
      right: 20px;
      bottom: 10px;

      svg {
        transform: scale(0.6);
      }
    }
  `}
`;

export default Scholars;
